import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { EventInput, EventOccurrence, EventType, Person } from "@shared/types";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import { eventsApi, ApiError } from "@/api/client";
import { APP_TIMEZONE, fromDateValue, fromDatetimeLocalValue, toDateValue, toDatetimeLocalValue } from "@/utils/date";

export type EventFormInitial =
  | { kind: "create"; startIso: string; allDay: boolean }
  | { kind: "edit"; occurrence: EventOccurrence };

interface EventFormModalProps {
  open: boolean;
  initial: EventFormInitial | null;
  people: Person[];
  eventTypes: EventType[];
  onClose: () => void;
  onSaved: () => void;
}

const WEEKDAYS: { code: string; label: string }[] = [
  { code: "SU", label: "S" },
  { code: "MO", label: "M" },
  { code: "TU", label: "T" },
  { code: "WE", label: "W" },
  { code: "TH", label: "T" },
  { code: "FR", label: "F" },
  { code: "SA", label: "S" },
];

interface FormState {
  title: string;
  eventTypeId: string;
  participantIds: string[];
  team: string;
  opponent: string;
  locationName: string;
  address: string;
  notes: string;
  reminderNote: string;
  sourceUrl: string;
  allDay: boolean;
  startLocal: string;
  endLocal: string;
  repeatWeekly: boolean;
  repeatDays: string[];
  repeatInterval: number;
  repeatEnd: string;
  editScope: "occurrence" | "series";
}

function emptyForm(defaults: Partial<FormState> = {}): FormState {
  return {
    title: "",
    eventTypeId: "",
    participantIds: [],
    team: "",
    opponent: "",
    locationName: "",
    address: "",
    notes: "",
    reminderNote: "",
    sourceUrl: "",
    allDay: false,
    startLocal: "",
    endLocal: "",
    repeatWeekly: false,
    repeatDays: [],
    repeatInterval: 1,
    repeatEnd: "",
    editScope: "occurrence",
    ...defaults,
  };
}

function parseRRuleDays(rrule: string | null): string[] {
  if (!rrule) return [];
  const match = /BYDAY=([^;]+)/.exec(rrule);
  return match ? match[1].split(",") : [];
}

export function EventFormModal({ open, initial, people, eventTypes, onClose, onSaved }: EventFormModalProps) {
  const [form, setForm] = useState<FormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = initial?.kind === "edit";
  const editingOccurrence = initial?.kind === "edit" ? initial.occurrence : null;
  const isRecurring = Boolean(editingOccurrence?.rrule);

  useEffect(() => {
    if (!open || !initial) return;
    setError(null);

    if (initial.kind === "create") {
      const start = new Date(initial.startIso);
      const end = new Date(start.getTime() + 60 * 60 * 1000);
      setForm(
        emptyForm({
          eventTypeId: eventTypes[0]?.id ?? "",
          allDay: initial.allDay,
          startLocal: initial.allDay ? toDateValue(initial.startIso) : toDatetimeLocalValue(start.toISOString()),
          endLocal: initial.allDay ? toDateValue(initial.startIso) : toDatetimeLocalValue(end.toISOString()),
        }),
      );
    } else {
      const occ = initial.occurrence;
      setForm(
        emptyForm({
          title: occ.title,
          eventTypeId: occ.eventTypeId,
          participantIds: occ.participantIds,
          team: occ.team ?? "",
          opponent: occ.opponent ?? "",
          locationName: occ.locationName ?? "",
          address: occ.address ?? "",
          notes: occ.notes ?? "",
          reminderNote: occ.reminderNote ?? "",
          sourceUrl: occ.sourceUrl ?? "",
          allDay: occ.allDay,
          startLocal: occ.allDay ? toDateValue(occ.occurrenceStart) : toDatetimeLocalValue(occ.occurrenceStart),
          endLocal: occ.allDay ? toDateValue(occ.endAt) : toDatetimeLocalValue(occ.endAt),
          repeatWeekly: Boolean(occ.rrule),
          repeatDays: parseRRuleDays(occ.rrule),
          repeatInterval: 1,
          repeatEnd: occ.recurrenceEndAt ? toDateValue(occ.recurrenceEndAt) : "",
          editScope: "occurrence",
        }),
      );
    }
  }, [open, initial, eventTypes]);

  if (!open || !initial) return null;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleParticipant(id: string) {
    setForm((f) => ({
      ...f,
      participantIds: f.participantIds.includes(id)
        ? f.participantIds.filter((p) => p !== id)
        : [...f.participantIds, id],
    }));
  }

  function toggleDay(code: string) {
    setForm((f) => ({
      ...f,
      repeatDays: f.repeatDays.includes(code) ? f.repeatDays.filter((d) => d !== code) : [...f.repeatDays, code],
    }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!form.title.trim()) return setError("Title is required");
    if (!form.eventTypeId) return setError("Choose an event type");
    if (form.participantIds.length === 0) return setError("Choose at least one participant");
    if (form.repeatWeekly && form.repeatDays.length === 0) return setError("Choose at least one day to repeat on");

    const startAt = form.allDay ? fromDateValue(form.startLocal) : fromDatetimeLocalValue(form.startLocal);
    const endAt = form.allDay ? fromDateValue(form.endLocal) : fromDatetimeLocalValue(form.endLocal);

    if (new Date(endAt).getTime() < new Date(startAt).getTime()) {
      return setError("End must be after start");
    }

    const input: EventInput = {
      title: form.title.trim(),
      eventTypeId: form.eventTypeId,
      participantIds: form.participantIds,
      team: form.team.trim() || null,
      opponent: form.opponent.trim() || null,
      locationName: form.locationName.trim() || null,
      address: form.address.trim() || null,
      notes: form.notes.trim() || null,
      reminderNote: form.reminderNote.trim() || null,
      sourceUrl: form.sourceUrl.trim() || null,
      startAt,
      endAt,
      allDay: form.allDay,
      timezone: APP_TIMEZONE,
      rrule: form.repeatWeekly
        ? `FREQ=WEEKLY;INTERVAL=${form.repeatInterval};BYDAY=${form.repeatDays.join(",")}`
        : null,
      recurrenceEndAt: form.repeatWeekly && form.repeatEnd ? fromDateValue(form.repeatEnd) : null,
    };

    setSubmitting(true);
    try {
      if (isEdit && editingOccurrence) {
        await eventsApi.update(
          editingOccurrence.id,
          input,
          isRecurring ? form.editScope : "series",
          editingOccurrence.occurrenceStart,
        );
      } else {
        await eventsApi.create(input);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save event");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} width="lg">
      <form onSubmit={handleSubmit} className="flex max-h-[90vh] flex-col">
        <div className="flex items-center justify-between border-b border-line dark:border-line-dark px-6 py-4">
          <h2 className="text-lg font-semibold">{isEdit ? "Edit event" : "Add event"}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-1.5 hover:bg-black/5 dark:hover:bg-white/10">
            ✕
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {isEdit && isRecurring && (
            <div className="rounded-lg border border-line dark:border-line-dark p-3">
              <p className="mb-2 text-xs font-medium text-muted dark:text-muted-dark">Apply changes to</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => update("editScope", "occurrence")}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${form.editScope === "occurrence" ? "border-accent bg-accent/10 text-accent" : "border-line dark:border-line-dark text-muted dark:text-muted-dark"}`}
                >
                  Just this event
                </button>
                <button
                  type="button"
                  onClick={() => update("editScope", "series")}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${form.editScope === "series" ? "border-accent bg-accent/10 text-accent" : "border-line dark:border-line-dark text-muted dark:text-muted-dark"}`}
                >
                  Entire series
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-muted dark:text-muted-dark">Title</label>
            <input
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="e.g. Game vs Barça Academy"
              className="mt-1 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted dark:text-muted-dark">Event type</label>
              <select
                value={form.eventTypeId}
                onChange={(e) => update("eventTypeId", e.target.value)}
                className="mt-1 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
              >
                <option value="" disabled>
                  Choose a type
                </option>
                {eventTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted dark:text-muted-dark">Participants</label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {people.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => toggleParticipant(p.id)}
                    className="rounded-full border px-2.5 py-1 text-xs font-medium transition-colors"
                    style={
                      form.participantIds.includes(p.id)
                        ? { backgroundColor: p.color, borderColor: p.color, color: "white" }
                        : { borderColor: "var(--tw-border-opacity, #e8e8e6)" }
                    }
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted dark:text-muted-dark">Team / organization</label>
              <input
                value={form.team}
                onChange={(e) => update("team", e.target.value)}
                className="mt-1 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted dark:text-muted-dark">Opponent</label>
              <input
                value={form.opponent}
                onChange={(e) => update("opponent", e.target.value)}
                className="mt-1 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.allDay} onChange={(e) => update("allDay", e.target.checked)} />
            All-day event
          </label>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted dark:text-muted-dark">Starts</label>
              <input
                type={form.allDay ? "date" : "datetime-local"}
                value={form.startLocal}
                onChange={(e) => update("startLocal", e.target.value)}
                className="mt-1 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted dark:text-muted-dark">Ends</label>
              <input
                type={form.allDay ? "date" : "datetime-local"}
                value={form.endLocal}
                onChange={(e) => update("endLocal", e.target.value)}
                className="mt-1 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
          </div>

          {(!isEdit || form.editScope === "series") && (
            <div className="rounded-lg border border-line dark:border-line-dark p-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={form.repeatWeekly}
                  onChange={(e) => update("repeatWeekly", e.target.checked)}
                />
                Repeats weekly
              </label>
              {form.repeatWeekly && (
                <div className="mt-3 space-y-3">
                  <div className="flex gap-1.5">
                    {WEEKDAYS.map((d) => (
                      <button
                        type="button"
                        key={d.code}
                        onClick={() => toggleDay(d.code)}
                        className={`h-8 w-8 rounded-full text-xs font-semibold ${form.repeatDays.includes(d.code) ? "bg-accent text-white" : "bg-black/5 dark:bg-white/10 text-muted dark:text-muted-dark"}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted dark:text-muted-dark">Ends on (optional)</label>
                    <input
                      type="date"
                      value={form.repeatEnd}
                      onChange={(e) => update("repeatEnd", e.target.value)}
                      className="mt-1 w-full max-w-xs rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-muted dark:text-muted-dark">Location</label>
            <input
              value={form.locationName}
              onChange={(e) => update("locationName", e.target.value)}
              placeholder="Field / venue name"
              className="mt-1 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
            />
            <input
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              placeholder="Full address (for directions)"
              className="mt-2 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted dark:text-muted-dark">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted dark:text-muted-dark">Reminder note</label>
              <input
                value={form.reminderNote}
                onChange={(e) => update("reminderNote", e.target.value)}
                placeholder="e.g. Bring cleats and shin guards"
                className="mt-1 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted dark:text-muted-dark">Source link</label>
              <input
                value={form.sourceUrl}
                onChange={(e) => update("sourceUrl", e.target.value)}
                placeholder="https://…"
                className="mt-1 w-full rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
          </div>

          {error && <p className="text-sm font-medium text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 border-t border-line dark:border-line-dark px-6 py-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? "Saving…" : "Save event"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
